import { IconLogout, IconPlus } from '@tabler/icons-react';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { v4 as uuid } from 'uuid';

import useSignOut from 'api/logout/logout';
import {
  LinkKeepParams,
  useNavigateKeepParams,
} from 'components/KeepParamsRouter/KeepParamsRouter';
import CacheRefresh from 'components/SideNavbar/CacheRefresh';
import { POPOVER_MENU_DEFS } from 'components/SideNavbar/SidebarPopover/SidebarPopover.defs';
import SidebarPopoverRow from 'components/SideNavbar/SidebarPopover/SidebarPopoverRow';
import { PopoeverMenuKeys } from 'components/SideNavbar/SideNavbar';
import useFeatureFlag from 'hooks/useFeatureFlag';
import Badge from '@lotlinx-aws/sparky-component-library/Sparky/Badge';
import Button from '@lotlinx-aws/sparky-component-library/Sparky/Button';
import Flex from '@lotlinx-aws/sparky-component-library/Sparky/Flex';
import IconButton from '@lotlinx-aws/sparky-component-library/Sparky/IconButton';
import { COLOR } from '@lotlinx-aws/sparky-component-library/Sparky/styles/vars';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import { userAtom } from 'store';
import { FeatureFlag } from 'types/FeatureFlags';

import styles from './SidebarPopover.module.scss';
import { useLocation } from 'react-router';
import { useSelectedDealer } from '@lotlinx-aws/dealer-selector';
import MarketInsightsSidebarNotification from 'components/SideNavbar/MarketInsightsSidebarNotification/MarketInsightsSidebarNotification';

export interface SidebarPopoverProps {
  activeKey?: PopoeverMenuKeys | undefined;
  closeMenu: () => void;
  campaignNotificationNumber?: number;
}

/**
 * SidebarPopover
 */
export default function SidebarPopover({
  activeKey,
  closeMenu,
  campaignNotificationNumber,
}: SidebarPopoverProps) {
  const location = useLocation();
  const [user, setUser] = useAtom(userAtom);
  const showAdminActivityFlag = useFeatureFlag(FeatureFlag.ENABLE_ADMIN_ACTIVITY);
  const isMarketInsightsV2Enabled = useFeatureFlag(FeatureFlag.ENABLE_MARKET_INSIGHTS_V2);
  const showAdminActivity = user?.accountType === 'OWNER' && showAdminActivityFlag;
  const dealer = useSelectedDealer();
  const mutation = useSignOut();
  const navigate = useNavigateKeepParams();

  const handleSignOut = () => {
    setUser(undefined);
    if (user) {
      mutation.mutate(user.token);
    }
  };

  if (!activeKey || activeKey === 'more') {
    return <></>;
  }
  const clickHandler = (location: string) => {
    navigate(location);
    closeMenu();
  };

  return (
    <div className={styles.popoverContent}>
      <div className={styles.topContainer}>
        <div className={styles.header}>
          <Text fontWeight={7} size={20}>
            VIN Manager
          </Text>
        </div>
        <Flex padding='16px' column width='100%'>
          {Object.keys(POPOVER_MENU_DEFS[activeKey]).map((key) => {
            // hide admin functionality for non-admins
            if (key === 'Admin' && !showAdminActivity) {
              return <></>;
            }

            return (
              <div className={styles.linkList} key={key}>
                <div className={styles.title}>
                  <Text type='label-01'>{key}</Text>
                </div>
                {POPOVER_MENU_DEFS[activeKey][key].length > 0 && (
                  <>
                    {POPOVER_MENU_DEFS[activeKey][key].map((link) => {
                      return (
                        <SidebarPopoverRow
                          key={uuid()}
                          link={link}
                          clickHandler={() => clickHandler(link.location)}
                          badge={
                            link.title === 'Performance Results' && campaignNotificationNumber ? (
                              <Badge
                                className={classNames([
                                  styles.badgeContainer,
                                  campaignNotificationNumber.toString().length > 2 && styles.large,
                                ])}
                              >
                                <Text as='div' lineHeight='1' size={12} fontWeight={5}>
                                  {campaignNotificationNumber}
                                </Text>
                              </Badge>
                            ) : undefined
                          }
                        />
                      );
                    })}
                  </>
                )}
              </div>
            );
          })}
          {activeKey === 'campaigns' && (
            <>
              <LinkKeepParams to={'/campaign_creation'} className={styles.link}>
                <Button
                  className={styles.createCampaignButton}
                  variant='outline'
                  disabled={location.pathname === '/campaign_creation'}
                  data-track='nav_popover_campaign_create'
                  data-track-section='sidebar_popover'
                  data-track-target='navigate'
                >
                  <IconPlus size={20} /> Create a campaign
                </Button>
              </LinkKeepParams>
            </>
          )}
          {activeKey === 'dealerManagement' && <CacheRefresh />}
        </Flex>
      </div>
      <Flex width='100%' column gap='24px'>
        {activeKey === 'analytics' && isMarketInsightsV2Enabled && (
          <MarketInsightsSidebarNotification />
        )}
        <div className={styles.bottomContainer}>
          <Flex column rowGap='8px'>
            <Text size='14'>
              {user?.first} {user?.last}
            </Text>
            <Text size='14' color='secondary'>
              {user?.email}
            </Text>
          </Flex>

          <IconButton
            aria-label='logout button'
            size='lg'
            tooltip='Logout'
            onClick={handleSignOut}
            data-track='nav_popover_logout'
            data-track-section='sidebar_popover'
            data-track-target='sign_out'
          >
            <IconLogout size={24} color={COLOR.SPARKY_NAVY_400} />
          </IconButton>
        </div>
      </Flex>
    </div>
  );
}
